import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  Inbox, AlertTriangle, BriefcaseMedical, Video,
  ChevronRight,
} from 'lucide-react';

/* ═══ MOCK DATA ═══ */
const CODA_TRIAGE = [
  { id: 1, paziente: 'Colombo Maria', eta: 88, provenienza: 'MMG — Dott. Bianchi', tipologia: 'ADI Complessa (CIA-4)', priorita: 'alta', data: '15/04/2026', motivazione: 'Paziente con demenza senile avanzata, necessita assistenza domiciliare integrata per supporto attività quotidiane e monitoraggio parametri vitali. Caregiver in burnout.', cf: 'CLMMRA38T41H501E', diagnosi: 'Demenza senile avanzata', caregiver: 'Figlio — Marco Colombo', telefono: '+39 328 9876543' },
  { id: 2, paziente: 'Bianchi Mario', eta: 74, provenienza: 'MMG — Dott.ssa Verdi', tipologia: 'ADI Dimissione Protetta (CIA-3)', priorita: 'alta', data: '14/04/2026', motivazione: 'Dimissione da Policlinico Gemelli post-ictus ischemico. Necessaria riabilitazione domiciliare intensiva e monitoraggio neurologico.', cf: 'BNCMRA52B12H501G', diagnosi: 'Ictus ischemico — post-acuto', caregiver: 'Moglie — Anna Bianchi', telefono: '+39 339 1122334' },
  { id: 3, paziente: 'Ferri Laura', eta: 66, provenienza: 'Ospedale — S. Camillo', tipologia: 'ADI Infermieristica (CIA-2)', priorita: 'media', data: '13/04/2026', motivazione: 'Paziente diabetica con ulcera venosa cronica al polpaccio sinistro. Necessarie medicazioni avanzate 3/sett a domicilio.', cf: 'FRRLRA60C50H501H', diagnosi: 'Ulcera venosa cronica', caregiver: 'Marito — Franco Ferri', telefono: '+39 340 5566778' },
  { id: 4, paziente: 'Mancini Rosa', eta: 91, provenienza: 'MMG — Dott. Esposito', tipologia: 'ADI Cure Palliative (CIA-4)', priorita: 'alta', data: '12/04/2026', motivazione: 'Neoplasia polmonare stadio IV. Richiesta cure palliative domiciliari e supporto psicologico per la famiglia.', cf: 'MNCRSA35T51H501I', diagnosi: 'K polmone stadio IV', caregiver: 'Figlia — Lucia Mancini', telefono: '+39 347 8899001' },
];

const ALLARMI_CLINICI = [
  { id: 1, paziente: 'Verdi Luca', tipo: 'SpO2 sotto soglia', valore: '88%', soglia: '< 92%', ora: '08:30', severita: 'critica' },
  { id: 2, paziente: 'Rossi Anna', tipo: 'PA Sistolica elevata', valore: '178 mmHg', soglia: '> 160 mmHg', ora: '09:15', severita: 'alta' },
  { id: 3, paziente: 'Ferrara Elena', tipo: 'FC anomala', valore: '112 bpm', soglia: '> 100 bpm', ora: '10:00', severita: 'media' },
  { id: 4, paziente: 'Esposito Luca', tipo: 'Glicemia elevata', valore: '320 mg/dL', soglia: '> 250 mg/dL', ora: '07:45', severita: 'alta' },
  { id: 5, paziente: 'Neri Giuseppe', tipo: 'Temperatura corporea', valore: '38.9°C', soglia: '> 38.5°C', ora: '11:20', severita: 'media' },
];

const MEDICI_UVMD = ['Dott. R. Marino', 'Dott.ssa F. Lombardi', 'Dott. A. De Santis', 'Dott.ssa P. Greco'];
const CASE_MANAGERS = ['Inf. L. Conti', 'Inf. M. Ferrara', 'Inf. A. Romano', 'Inf. G. Neri'];
const SPECIALISTI = ['Fisiatra — Dott. E. Tosi', 'Assistente Sociale — Dott.ssa B. Villa', 'Psicologo — Dott. C. Marini', 'Logopedista — Dott.ssa S. Ricci', 'Nutrizionista — Dott. L. Fontana'];

const PRIO_STYLE = {
  alta: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  media: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  bassa: { bg: 'bg-slate-100 dark:bg-slate-700/40', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400' },
};

const SEV_STYLE = {
  critica: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', ring: 'ring-red-200 dark:ring-red-800/40' },
  alta: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', ring: 'ring-orange-200 dark:ring-orange-800/40' },
  media: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', ring: 'ring-amber-200 dark:ring-amber-800/40' },
};

export default function CotControlRoom() {
  const navigate = useNavigate();

  return (
    <AppLayout role="segreteria">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="cot-control-room">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="cot-title">Dashboard Centrale</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Centrale Operativa Territoriale — Monitoraggio in tempo reale</p>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cot-kpi-strip">
          {[
            { label: 'Nuovi Triage da Valutare', value: CODA_TRIAGE.length, icon: Inbox, color: '#3B82F6', bg: 'bg-blue-50 dark:bg-blue-950/30' },
            { label: 'Allarmi Clinici Attivi', value: ALLARMI_CLINICI.length, icon: AlertTriangle, color: '#EF4444', bg: 'bg-red-50 dark:bg-red-950/30' },
            { label: 'Dimissioni Protette (H→T)', value: 3, icon: BriefcaseMedical, color: '#F97316', bg: 'bg-orange-50 dark:bg-orange-950/30' },
            { label: 'Teleconsulti in Programma', value: 8, icon: Video, color: '#10B981', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
          ].map(kpi => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex items-center gap-3" data-testid={`kpi-${kpi.label.substring(0,10).toLowerCase().replace(/\s/g,'-')}`}>
                <div className={`w-11 h-11 rounded-xl ${kpi.bg} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5" style={{ color: kpi.color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-white">{kpi.value}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">{kpi.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* LEFT: Coda Triage */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="coda-triage">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <Inbox className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-800 dark:text-white">Coda Triage In Entrata</h2>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">{CODA_TRIAGE.length} pratiche in attesa</p>
                </div>
              </div>
              <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-0 text-[10px] font-bold">{CODA_TRIAGE.filter(t => t.priorita === 'alta').length} urgenti</Badge>
            </div>
            <div className="flex flex-col gap-0">
              {CODA_TRIAGE.map(t => {
                const ps = PRIO_STYLE[t.priorita];
                return (
                  <div key={t.id} className="px-5 py-4 border-b border-slate-100 dark:border-slate-800/60 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`triage-row-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">{t.paziente}</p>
                          <Badge className={`${ps.bg} ${ps.text} border-0 text-[9px] font-bold gap-1 px-2`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${ps.dot}`} />
                            {t.priorita}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t.eta} anni · {t.tipologia}</p>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Provenienza: {t.provenienza} · {t.data}</p>
                      </div>
                      <button
                        onClick={() => navigate(`/segreteria/richiesta/${t.id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors shrink-0"
                        data-testid={`gestisci-${t.id}`}
                      >
                        Gestisci <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Log Allarmi Clinici */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="log-allarmi">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-800 dark:text-white">Log Allarmi Clinici</h2>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Telemonitoraggio — Alert attivi oggi</p>
                </div>
              </div>
              <Badge className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-0 text-[10px] font-bold animate-pulse">{ALLARMI_CLINICI.filter(a => a.severita === 'critica').length} critici</Badge>
            </div>
            <div className="flex flex-col gap-0">
              {ALLARMI_CLINICI.map(a => {
                const sv = SEV_STYLE[a.severita];
                return (
                  <div key={a.id} className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800/60 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`allarme-row-${a.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-lg ${sv.bg} ring-1 ${sv.ring} flex items-center justify-center shrink-0`}>
                          <AlertTriangle className={`w-3.5 h-3.5 ${sv.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">{a.paziente}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{a.tipo}: <strong className={sv.text}>{a.valore}</strong> <span className="text-slate-400">(soglia {a.soglia})</span></p>
                        </div>
                      </div>
                      <div className="shrink-0">
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">{a.ora}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </AppLayout>
  );
}
